import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { AssistantProvider } from './contexts/AssistantContext';
import TenantBrandingEffect from './components/common/TenantBrandingEffect';
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicRoute from './components/common/PublicRoute';
import HomeRedirect from './components/common/HomeRedirect';
import { PERM } from './auth/permissions';
import AppLayout from './components/layouts/AppLayout';
import CoursePlayerLayout from './components/layouts/CoursePlayerLayout';
// Learner (consumer) course-flow pages — now everyone's "my learning" surface.
import LearnerDashboardPage from './pages/learner/DashboardPage';
import LearnerCoursesPage from './pages/learner/CoursesPage';
import LearnerCourseDetailPage from './pages/learner/CourseDetailPage';
import LearnerEvaluationPlayerPage from './pages/learner/EvaluationPlayerPage';
import LearnerFeedbackPage from './pages/learner/FeedbackPage';
import LearnerProgramsPage from './pages/learner/ProgramsPage';
import LearnerProgramDetailPage from './pages/learner/ProgramDetailPage';
import LearnerProgramOnboardingDonePage from './pages/learner/ProgramOnboardingDonePage';
import LearnerCertificatesPage from './pages/learner/CertificatesPage';
import LearnerProfilePage from './pages/learner/ProfilePage';
import LearnerSettingsPage from './pages/learner/SettingsPage';
import LearnerPrizesPage from './pages/learner/PrizesPage';
import LearnerPrizeHistoryPage from './pages/learner/PrizeHistoryPage';
import LearnerPrizeClaimPage from './pages/learner/PrizeClaimPage';
import LearnerProgramDayDetailPage from './pages/learner/ProgramDayDetailPage';
import LearnerCourseModulePage from './pages/learner/CourseModulePage';
import LearnerCourseFinishPage from './pages/learner/CourseFinishPage';
import LearnerCourseSectionsPage from './pages/learner/CourseSectionsPage';
import LearnerCourseSectionModulesPage from './pages/learner/CourseSectionModulesPage';
import LearnerCalendarPage from './pages/learner/CalendarPage';
import ChatPage from './pages/chat/ChatPage';
import CalendarPage from './pages/calendar/CalendarPage';
import MeIntegrationsRedirect from './pages/redirects/MeIntegrationsRedirect';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import SelectWorkspacePage from './pages/auth/SelectWorkspacePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import EnterCodePage from './pages/auth/EnterCodePage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import EmailUnsubscribePage from './pages/email/UnsubscribePage';
import DashboardHomePage from './pages/dashboard/DashboardHomePage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import GamificationPage from './pages/gamification/GamificationPage';
import SettingsPage from './pages/settings/SettingsPage';
import CoursesListPage from './pages/courses/CoursesListPage';
import CourseCreationWizard from './pages/courses/CourseCreationWizard';
import CourseCreationIntroPage from './pages/courses/CourseCreationIntroPage';
import AICourseGeneratePage from './pages/courses/AICourseGeneratePage';
import CourseEditPage from './pages/courses/CourseEditPage';
import CourseConfigurePage from './pages/courses/CourseConfigurePage';
import CoursePreviewPage from './pages/courses/CoursePreviewPage';
import LearnersListPage from './pages/learners/LearnersListPage';
import LearnerDetailsPage from './pages/learners/LearnerDetailsPage';
import LearnerGroupsPage from './pages/learners/LearnerGroupsPage';
import LearnerCreatePage from './pages/learners/LearnerCreatePage';
import LearnerBulkUploadPage from './pages/learners/LearnerBulkUploadPage';
import PrizesListPage from './pages/prizes/PrizesListPage';
import PrizeCreatePage from './pages/prizes/PrizeCreatePage';
import PrizeDetailPage from './pages/prizes/PrizeDetailPage';
import ProgramsListPage from './pages/programs/ProgramsListPage';
import ProgramCreationWizard from './pages/programs/wizard/ProgramCreationWizard';
import ProgramDetailPage from './pages/programs/ProgramDetailPage';
import ProgramEditPage from './pages/programs/ProgramEditPage';
import ProgramConfigurePage from './pages/programs/ProgramConfigurePage';
import ProgramPreviewWelcomePage from './pages/programs/ProgramPreviewWelcomePage';
import ProgramPreviewDaysPage from './pages/programs/ProgramPreviewDaysPage';
import ProgramPreviewDayDetailPage from './pages/programs/ProgramPreviewDayDetailPage';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { queryClient } from './lib/queryClient';
import { GOOGLE_CONFIG } from './config/env';
import { useChatSocket } from './hooks/chat/useChatSocket';
import { useChatToastNotifier } from './hooks/chat/useChatToastNotifier';
import { useNotificationsSocket } from './hooks/useNotificationsSocket';

// Mounted once at the App root so the chat socket runs on every page —
// powering the live ChatBadge and in-app toast notifier even when the user
// isn't on /chat. The notification socket shares the same connection and keeps
// the bell badge + notifications panel live. `useChatAuthToken()` returns null
// when unauthenticated, so the underlying effects are no-ops until login.
const ChatLifecycle: React.FC = () => {
  useChatSocket();
  useChatToastNotifier();
  useNotificationsSocket();
  return null;
};

// Redirects /settings/<sub> to /settings?tab=<sub> while preserving the search
// string (needed so OAuth callback query params land on the page intact).
const SettingsTabRedirect: React.FC<{ tab: string }> = ({ tab }) => {
  const location = useLocation();
  const incoming = new URLSearchParams(location.search);
  incoming.set('tab', tab);
  return <Navigate to={`/settings?${incoming.toString()}`} replace />;
};

// Helper: wrap a management page (gated by permission) in the unified shell.
const manage = (element: React.ReactNode, perm?: typeof PERM[keyof typeof PERM]) => (
  <ProtectedRoute requirePerm={perm}>
    <AppLayout>{element}</AppLayout>
  </ProtectedRoute>
);

// Helper: wrap a consumer (any-member) page in the unified shell.
const consume = (element: React.ReactNode) => (
  <ProtectedRoute>
    <AppLayout>{element}</AppLayout>
  </ProtectedRoute>
);

// Helper: wrap a course-taking page in the immersive course-player shell.
const player = (element: React.ReactNode) => (
  <ProtectedRoute>
    <CoursePlayerLayout>{element}</CoursePlayerLayout>
  </ProtectedRoute>
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <SidebarProvider>
              <ViewModeProvider>
              <GoogleOAuthProvider clientId={GOOGLE_CONFIG.clientId || ''}>
                <AssistantProvider>
                <TenantBrandingEffect />
                <ChatLifecycle />
                <Routes>
                  {/* ── Public (unauthenticated) routes ── */}
                  <Route path="/auth/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                  <Route path="/auth/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
                  <Route path="/auth/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
                  <Route path="/auth/enter-code" element={<PublicRoute><EnterCodePage /></PublicRoute>} />
                  <Route path="/auth/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
                  {/* Workspace picker — authenticated-but-not-yet-scoped (pre-auth session). */}
                  <Route path="/auth/select-workspace" element={<SelectWorkspacePage />} />
                  <Route path="/access-denied" element={<AccessDeniedPage />} />
                  {/* Email unsubscribe link — no guard at all: it must open the same
                      signed in or out (PublicRoute would bounce members away). */}
                  <Route path="/email/unsubscribe" element={<EmailUnsubscribePage />} />

                  {/* ── Management surface (permission-gated) ── */}
                  <Route path="/dashboard" element={consume(<DashboardHomePage />)} />
                  <Route path="/analytics" element={manage(<AnalyticsPage />, PERM.ANALYTICS_VIEW)} />
                  <Route path="/gamification" element={manage(<GamificationPage />, PERM.GAMIFICATION_MANAGE)} />

                  <Route path="/courses" element={manage(<CoursesListPage />, PERM.COURSE_AUTHOR)} />
                  <Route path="/courses/create" element={manage(<CourseCreationIntroPage />, PERM.COURSE_AUTHOR)} />
                  <Route path="/courses/new" element={manage(<CourseCreationWizard />, PERM.COURSE_AUTHOR)} />
                  <Route path="/courses/ai" element={manage(<AICourseGeneratePage />, PERM.COURSE_AI_GENERATE)} />
                  <Route path="/courses/:id/edit" element={manage(<CourseEditPage />, PERM.COURSE_AUTHOR)} />
                  <Route path="/courses/:id/configure" element={manage(<CourseConfigurePage />, PERM.COURSE_AUTHOR)} />
                  {/* `new` = create wizard; any other value = edit that draft assignment. */}
                  <Route path="/courses/:id/configure/:assignmentId" element={manage(<CourseConfigurePage />, PERM.COURSE_AUTHOR)} />
                  {/* Full "view as learner" preview of (possibly unpublished) course
                      content — immersive course-player shell (no admin sidebar),
                      COURSE_AUTHOR-gated. */}
                  <Route
                    path="/courses/:id/preview"
                    element={
                      <ProtectedRoute requirePerm={PERM.COURSE_AUTHOR}>
                        <CoursePlayerLayout>
                          <CoursePreviewPage />
                        </CoursePlayerLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/programs" element={manage(<ProgramsListPage />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/new" element={manage(<ProgramCreationWizard />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/:id/edit" element={manage(<ProgramEditPage />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/:id/configure" element={manage(<ProgramConfigurePage />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/:id/preview" element={manage(<ProgramPreviewWelcomePage />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/:id/preview/days" element={manage(<ProgramPreviewDaysPage />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/:id/preview/days/:dayId" element={manage(<ProgramPreviewDayDetailPage />, PERM.PROGRAM_MANAGE)} />
                  <Route path="/programs/:id" element={manage(<ProgramDetailPage />, PERM.PROGRAM_MANAGE)} />

                  <Route path="/prizes" element={manage(<PrizesListPage />, PERM.PRIZE_MANAGE)} />
                  <Route path="/prizes/new" element={manage(<PrizeCreatePage />, PERM.PRIZE_MANAGE)} />
                  <Route path="/prizes/:id/edit" element={manage(<PrizeCreatePage />, PERM.PRIZE_MANAGE)} />
                  <Route path="/prizes/:id" element={manage(<PrizeDetailPage />, PERM.PRIZE_MANAGE)} />

                  <Route path="/learners" element={manage(<LearnersListPage />, PERM.MEMBER_MANAGE)} />
                  <Route path="/learners/new" element={manage(<LearnerCreatePage />, PERM.MEMBER_MANAGE)} />
                  <Route path="/learners/bulk" element={manage(<LearnerBulkUploadPage />, PERM.MEMBER_MANAGE)} />
                  <Route path="/learners/groups" element={manage(<LearnerGroupsPage />, PERM.MEMBER_MANAGE)} />
                  <Route path="/learners/:id" element={manage(<LearnerDetailsPage />, PERM.MEMBER_MANAGE)} />

                  {/* Chat / Calendar / Settings — any authenticated member; staff
                      affordances inside are gated with <Can>. */}
                  <Route path="/chat" element={consume(<ChatPage />)} />
                  <Route path="/calendar" element={consume(<CalendarPage />)} />
                  <Route path="/settings" element={manage(<SettingsPage />, PERM.TENANT_MANAGE)} />
                  <Route path="/settings/general" element={<Navigate to="/settings?tab=general" replace />} />
                  <Route path="/settings/billing" element={<Navigate to="/settings?tab=billing" replace />} />
                  <Route path="/settings/notifications" element={<Navigate to="/settings?tab=notifications" replace />} />
                  <Route path="/settings/security" element={<Navigate to="/settings?tab=security" replace />} />
                  <Route path="/settings/integrations" element={<SettingsTabRedirect tab="integrations" />} />

                  {/* ── Universal learning surface (/learn/*) — any member ── */}
                  <Route path="/learn/dashboard" element={consume(<LearnerDashboardPage />)} />
                  <Route path="/learn/courses" element={consume(<LearnerCoursesPage />)} />
                  {/* Points & Prizes merged into one surface (product decision
                      2026-07-07); the old points URL keeps working. */}
                  <Route path="/learn/points" element={<Navigate to="/learn/prizes" replace />} />
                  <Route path="/learn/programs" element={consume(<LearnerProgramsPage />)} />
                  <Route path="/learn/programs/:programId" element={consume(<LearnerProgramDetailPage />)} />
                  <Route path="/learn/programs/:programId/days/:dayId" element={consume(<LearnerProgramDayDetailPage />)} />
                  <Route path="/learn/programs/:programId/done" element={consume(<LearnerProgramOnboardingDonePage />)} />
                  <Route path="/learn/certificates" element={consume(<LearnerCertificatesPage />)} />
                  <Route path="/learn/profile" element={consume(<LearnerProfilePage />)} />
                  <Route path="/learn/settings" element={consume(<LearnerSettingsPage />)} />
                  <Route path="/learn/prizes" element={consume(<LearnerPrizesPage />)} />
                  <Route path="/learn/prizes/history" element={consume(<LearnerPrizeHistoryPage />)} />
                  <Route path="/learn/prizes/:prizeId/claim" element={consume(<LearnerPrizeClaimPage />)} />
                  <Route path="/learn/chat" element={consume(<ChatPage />)} />
                  <Route path="/learn/calendar" element={consume(<LearnerCalendarPage />)} />

                  {/* Immersive course-taking flow — focused course-player shell. */}
                  <Route path="/learn/courses/:assignmentId" element={player(<LearnerCourseDetailPage />)} />
                  <Route path="/learn/courses/:assignmentId/done" element={player(<LearnerCourseFinishPage />)} />
                  <Route path="/learn/courses/:assignmentId/sections" element={player(<LearnerCourseSectionsPage />)} />
                  <Route path="/learn/courses/:assignmentId/sections/:sectionId" element={player(<LearnerCourseSectionsPage />)} />
                  <Route path="/learn/courses/:assignmentId/sections/:sectionId/modules" element={player(<LearnerCourseSectionModulesPage />)} />
                  <Route path="/learn/courses/:assignmentId/sections/:sectionId/modules/:moduleId" element={player(<LearnerCourseModulePage />)} />
                  <Route path="/learn/courses/:assignmentId/sections/:sectionId/modules/:moduleId/evaluate" element={player(<LearnerEvaluationPlayerPage />)} />
                  <Route path="/learn/courses/:assignmentId/sections/:sectionId/modules/:moduleId/feedback" element={player(<LearnerFeedbackPage />)} />

                  {/* Misc redirects */}
                  <Route path="/me/integrations" element={<MeIntegrationsRedirect />} />
                  <Route path="/learn" element={<Navigate to="/learn/dashboard" replace />} />
                  {/* Smart home: routes each member to their resolved view mode. */}
                  <Route path="/" element={<HomeRedirect />} />
                  {/* Catch-all: unknown URLs land home instead of a blank screen. */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </AssistantProvider>
              </GoogleOAuthProvider>
              </ViewModeProvider>
            </SidebarProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
